import { lazy, useEffect } from 'react';

// Stale-deploy self-healing: a tab that loaded an old build asks for chunk hashes the new build no longer has.
// 1) lazyRetry re-tries the import once, then reloads the page ONE time (cache-busting, sessionStorage-guarded) before giving up.
// 2) startVersionWatch notices a new build (asset-manifest main.js hash) and useStaleReload refreshes on the next route change — silently.
const RELOAD_KEY = 'veluro_chunk_reload_at';
const RELOAD_GUARD_MS = 30000;
const WATCH_EVERY_MS = 5 * 60 * 1000;

export const isChunkError = (e) => /ChunkLoadError|Loading chunk|Loading CSS chunk|Failed to fetch dynamically imported module|Importing a module script failed|error loading dynamically imported module/i.test(`${e?.name || ''} ${e?.message || ''}`);

export const reloadOnce = () => {
  let last = 0;
  try { last = Number(sessionStorage.getItem(RELOAD_KEY) || 0); } catch { /* ignore */ }
  if (Date.now() - last < RELOAD_GUARD_MS) return false;
  try { sessionStorage.setItem(RELOAD_KEY, String(Date.now())); } catch { /* ignore */ }
  const u = new URL(window.location.href);
  u.searchParams.set('_r', String(Date.now()));
  window.location.replace(u.toString());
  return true;
};

const wait = (ms) => new Promise((r) => setTimeout(r, ms));

export const lazyRetry = (importer) => lazy(async () => {
  try { return await importer(); }
  catch (e1) {
    if (!isChunkError(e1)) throw e1;
    await wait(800);
    try { return await importer(); }
    catch (e2) {
      if (isChunkError(e2) && reloadOnce()) return new Promise(() => {}); // page is reloading — keep Suspense pending instead of flashing an error
      throw e2;
    }
  }
});

let stale = false;
let watching = false;
const currentMain = () => (Array.from(document.scripts).map((s) => s.src || '').find((s) => /\/static\/js\/main\.[a-z0-9]+\.js/i.test(s)) || '');

const check = async () => {
  const cur = currentMain();
  if (!cur) return;
  try {
    const r = await fetch(`/asset-manifest.json?_=${Date.now()}`, { cache: 'no-store' });
    if (!r.ok) return;
    const j = await r.json();
    const next = j?.files?.['main.js'] || '';
    if (next && !cur.endsWith(next)) stale = true;
  } catch { /* offline or blocked — try again later */ }
};

export const startVersionWatch = () => {
  if (watching) return;
  watching = true;
  // Dynamic imports outside React's render (event handlers, prefetches) reject without reaching the ErrorBoundary.
  window.addEventListener('unhandledrejection', (ev) => { if (isChunkError(ev?.reason)) { ev.preventDefault(); reloadOnce(); } });
  if (process.env.NODE_ENV !== 'production') return;
  setInterval(check, WATCH_EVERY_MS);
  document.addEventListener('visibilitychange', () => { if (document.visibilityState === 'visible') check(); });
  window.addEventListener('online', check);
};

export const isStale = () => stale;

// On a route change after a new build was detected, pick up the new version (one full load, nothing typed on the old route is lost — they already left it).
export const useStaleReload = (pathname) => {
  useEffect(() => { if (stale) window.location.reload(); }, [pathname]);
};
